import { useLoginInfo } from '@/components/LoginInfo/provider';
import type { AdminInvoiceReportColumn } from '@/features/admin_invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ActionMenu,
  IconButton,
  IconMoreVert,
  LinkButton,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import styles from './ActionCell.module.scss';

const cx = classnames.bind(styles);

/** True when the row has at least one action the user may perform (3-dot is shown). */
const hasAnyVisibleAllowedAction = (record: AdminInvoiceReportColumn) => {
  const { edit_action, cancel_action, delete_action } = record;
  return (
    (edit_action.visible && edit_action.allowed) ||
    (cancel_action.visible && cancel_action.allowed) ||
    (delete_action.visible && delete_action.allowed)
  );
};

export type ActionCellProps = {
  record: AdminInvoiceReportColumn;
  onCancel?: (record: AdminInvoiceReportColumn) => void;
  onDelete?: (record: AdminInvoiceReportColumn) => void;
};

export const ActionCell: FC<ActionCellProps> = memo(
  ({ record, onCancel, onDelete }) => {
    const { t } = useTranslation();
    const { currentOfficeMember } = useLoginInfo();
    const isObserver =
      currentOfficeMember?.officeMemberSetting?.role === 'observer';
    const container = useGlobalContainerRef();
    const dropDownProps = useMemo(() => ({ destroyPopupOnHide: true }), []);

    const {
      menuItems,
      editMenuItemTooltip,
      cancelMenuItemTooltip,
      deleteMenuItemTooltip,
    } = useMemo(() => {
      const { edit_action, cancel_action, delete_action } = record;

      const editMenuItemTooltip = edit_action.allowed
        ? undefined
        : edit_action.reason ?? undefined;
      const cancelMenuItemTooltip = cancel_action.allowed
        ? undefined
        : cancel_action.reason ?? undefined;
      const deleteMenuItemTooltip = delete_action.allowed
        ? undefined
        : delete_action.reason ?? undefined;

      const items = [];

      if (edit_action.visible && edit_action.allowed) {
        items.push({
          key: 'edit',
          label: t('edit'),
          disabled: false,
          onClick: () => {
            window.location.href = record.edit_path;
          },
        });
      }

      if (cancel_action.visible) {
        items.push({
          key: 'cancel',
          label: t('admin_invoice_reports.actions.cancel'),
          disabled: !cancel_action.allowed,
          onClick: cancel_action.allowed ? () => onCancel?.(record) : undefined,
        });
      }

      if (delete_action.visible) {
        items.push({
          key: 'delete',
          label: t('admin_invoice_reports.actions.delete'),
          disabled: !delete_action.allowed,
          onClick: delete_action.allowed ? () => onDelete?.(record) : undefined,
        });
      }

      return {
        menuItems: items,
        editMenuItemTooltip,
        cancelMenuItemTooltip,
        deleteMenuItemTooltip,
      };
    }, [record, onCancel, onDelete, t]);

    const menuItemsWithStyleAndTooltips = useMemo(() => {
      return menuItems.map((item) => {
        if (item.key === 'edit') {
          const labelClass = editMenuItemTooltip
            ? 'actionLabelDisabled'
            : 'actionLabel';
          const labelSpan = (
            <span className={cx(labelClass)}>{item.label}</span>
          );
          if (editMenuItemTooltip) {
            return {
              ...item,
              label: (
                <Tooltip
                  title={editMenuItemTooltip}
                  overlayClassName={cx(styles['actionCellTooltip'])}
                  getPopupContainer={container}
                >
                  <div>{labelSpan}</div>
                </Tooltip>
              ),
            };
          }
          return { ...item, label: labelSpan };
        }

        if (item.key === 'cancel' || item.key === 'delete') {
          const tooltip =
            item.key === 'cancel'
              ? cancelMenuItemTooltip
              : deleteMenuItemTooltip;
          const labelClass = tooltip ? 'cancelLabelDisabled' : 'cancelLabel';
          const labelSpan = (
            <span className={cx(labelClass)}>{item.label}</span>
          );
          if (tooltip) {
            return {
              ...item,
              label: (
                <Tooltip
                  title={tooltip}
                  overlayClassName={cx(styles['actionCellTooltip'])}
                  getPopupContainer={container}
                >
                  <div>{labelSpan}</div>
                </Tooltip>
              ),
            };
          }
          return { ...item, label: labelSpan };
        }

        return item;
      });
    }, [
      menuItems,
      editMenuItemTooltip,
      cancelMenuItemTooltip,
      deleteMenuItemTooltip,
      container,
    ]);

    const shouldShowActionMenu =
      hasAnyVisibleAllowedAction(record) && !isObserver;

    return (
      <div
        className={cx(styles['container'])}
        onClick={(e) => e.stopPropagation()}
      >
        <LinkButton
          isTransparent
          href={record.detail_path}
          containerClassName={cx('detailLink')}
        >
          {t('admin_invoice_reports.table.columns.detail')}
        </LinkButton>
        <div
          className={cx('actionMenuContainer')}
          data-testid='admin-invoice-reports-action-cell-menu'
        >
          {shouldShowActionMenu && (
            <ActionMenu
              type='custom'
              items={menuItemsWithStyleAndTooltips}
              dropDownProps={dropDownProps}
              getPopupContainer={container}
              className={cx('actionMenu')}
            >
              <IconButton size='xs' inVisible>
                <IconMoreVert size={20} />
              </IconButton>
            </ActionMenu>
          )}
        </div>
      </div>
    );
  }
);

ActionCell.displayName = 'ActionCell';
